import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Calendar, Users, Star, Building2, Loader2, Package, Check } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";

interface EventOrganizer {
  id: string;
  name: string;
  company_name: string | null;
  category: string;
  description: string | null;
  image: string | null;
  total_events: number;
  rating: number;
  featured: boolean;
  contact_info: {
    email?: string;
    phone?: string;
  } | null;
  sponsorship_packages: Array<{
    name: string;
    price: number;
    benefits: string[];
  }> | null;
}

const EventDetail = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { language } = useLanguage();
  const [event, setEvent] = useState<EventOrganizer | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) return;

      try {
        const { data, error } = await supabase
          .from('event_organizers')
          .select('*')
          .eq('id', eventId)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setEvent({
            id: data.id,
            name: data.name,
            company_name: data.company_name,
            category: data.category,
            description: data.description,
            image: data.image,
            total_events: data.total_events,
            rating: data.rating,
            featured: data.featured,
            contact_info: data.contact_info as EventOrganizer['contact_info'],
            sponsorship_packages: data.sponsorship_packages as EventOrganizer['sponsorship_packages'],
          });
        }
      } catch (error) {
        console.error('Error fetching event:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 flex justify-center items-center min-h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <div className="max-w-md mx-auto p-8 rounded-2xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/10">
          <h1 className="text-2xl font-bold mb-4 text-primary">
            {language === 'ar' ? 'لم يتم العثور على الفعالية' : 'Event Not Found'}
          </h1>
          <p className="mb-8 text-muted-foreground">
            {language === 'ar' ? 'الفعالية التي تبحث عنها غير موجودة.' : 'The event you are looking for could not be found.'}
          </p>
          <Button asChild variant="outline">
            <Link to="/events-sponsors">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {language === 'ar' ? 'العودة للفعاليات' : 'Back to Events'}
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="container mx-auto px-4 py-12 max-w-5xl"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {/* Back Button */}
      <div className="mb-6">
        <Button variant="outline" asChild className="border-primary/10 hover:bg-primary/5">
          <Link to="/events-sponsors">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {language === 'ar' ? 'العودة للفعاليات' : 'Back to Events'}
          </Link>
        </Button>
      </div>

      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/5 via-background to-primary/10 p-8 shadow-lg border border-primary/10 mb-8">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full -mr-48 -mt-48 blur-3xl"></div>
        
        <div className="flex flex-col lg:flex-row gap-8 relative z-10">
          {/* Event Image */}
          <div className="w-full lg:w-80 flex-shrink-0">
            <div className="rounded-2xl overflow-hidden relative aspect-square shadow-xl border-2 border-primary/20">
              <img 
                src={event.image || "/placeholder.svg"} 
                alt={event.name} 
                className="w-full h-full object-cover"
              />
              {event.featured && (
                <Badge className="absolute top-4 right-4 bg-primary shadow-lg">
                  <Star className="w-3 h-3 mr-1" />
                  {language === 'ar' ? 'مميز' : 'Featured'}
                </Badge>
              )}
            </div>
          </div>

          {/* Event Info */}
          <div className="flex-1">
            <h1 className="text-3xl md:text-4xl font-bold mb-3 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              {event.name}
            </h1>
            
            {event.company_name && (
              <div className="flex items-center gap-2 text-muted-foreground mb-4">
                <Building2 className="h-4 w-4" />
                <span>{event.company_name}</span>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 mb-6">
              <Badge className="bg-primary/10 text-primary border-primary/20">
                {event.category}
              </Badge>
              <Badge variant="outline" className="flex items-center gap-1">
                <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                <span>{event.rating.toFixed(1)}</span>
              </Badge>
              <Badge variant="outline" className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                <span>{event.total_events} events</span>
              </Badge>
            </div>

            {event.description && (
              <p className="text-muted-foreground mb-6 leading-relaxed">
                {event.description}
              </p>
            )}

            <Button 
              size="lg" 
              className="bg-gradient-to-r from-primary to-primary/80 hover:shadow-lg transition-all"
              asChild
            >
              <Link to={`/events/${eventId}/sponsor`}>
                <Users className="mr-2 h-5 w-5" />
                {language === 'ar' ? 'كن راعياً' : 'Become a Sponsor'}
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Sponsorship Packages */}
      {event.sponsorship_packages && event.sponsorship_packages.length > 0 && (
        <Card className="border-primary/10 overflow-hidden relative">
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16 blur-3xl"></div>
          <CardContent className="pt-6 relative z-10">
            <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                {language === 'ar' ? 'حزم الرعاية' : 'Sponsorship Packages'}
              </span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {event.sponsorship_packages.map((pkg, index) => (
                <div 
                  key={index}
                  className="p-5 rounded-xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/10 hover:shadow-lg transition-all duration-300 hover:border-primary/20 flex flex-col"
                >
                  <h4 className="font-semibold text-lg text-foreground mb-2">{pkg.name}</h4>
                  
                  <div className="text-2xl font-bold text-primary mb-4">
                    {pkg.price.toLocaleString()} <span className="text-sm font-normal text-muted-foreground">SAR</span>
                  </div>

                  {pkg.benefits && pkg.benefits.length > 0 && (
                    <ul className="space-y-2 mb-4 flex-grow">
                      {pkg.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  <Button 
                    className="w-full bg-gradient-to-r from-primary to-primary/80 hover:shadow-md transition-all"
                    size="sm"
                    asChild
                  >
                    <Link to={`/events/${eventId}/sponsor?package=${encodeURIComponent(pkg.name)}`}>
                      {language === 'ar' ? 'اختر هذه الحزمة' : 'Choose This Package'}
                    </Link>
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
};

export default EventDetail;
